import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SectionCard, KpiCard, Loading, EmptyState, Money } from "@/components/common/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { ChevronRight, PiggyBank, Wallet, Coins, Receipt, Save, RotateCcw, Lock, Calendar, PieChart, Sparkles } from "lucide-react";

const VERT_ACCENT = {
  "ATL/BTL": "hsl(var(--chart-cat-4))",
  Digital: "hsl(var(--chart-cat-3))",
  CX: "hsl(var(--chart-cat-6))",
  Other: "hsl(var(--chart-cat-2))",
};

const mKey = (project, month) => `${project}||${month}`;
// stored values are ₹; the compact editor works in ₹ Lakhs for readability.
const toLakhStr = (rupees) => (rupees ? String(+(rupees / 1e5).toFixed(2)) : "0");
const lakhToRupees = (s) => {
  const n = parseFloat(s);
  return isNaN(n) ? 0 : Math.round(n * 1e5);
};
const spareCls = (v) => (v < 0 ? "text-[hsl(var(--danger))]" : "text-[hsl(var(--success))]");

export function BudgetAllocationTab() {
  const { isFull } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("monthly");
  const [open, setOpen] = useState({});
  const [edits, setEdits] = useState({}); // monthly edits: {proj||month: {mgmt_budget, mktg_mode, mktg_manual}}
  const [wedits, setWedits] = useState({}); // weight edits: {project: {ATL/BTL,...}}
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    return api.budgetMonthly()
      .then((d) => { setData(d); setLoading(false); })
      .catch(() => { setLoading(false); toast.error("Could not load monthly budgets"); });
  };

  useEffect(() => {
    let on = true; setLoading(true); setEdits({}); setWedits({});
    api.budgetMonthly()
      .then((d) => { if (on) { setData(d); setLoading(false); } })
      .catch(() => on && setLoading(false));
    return () => { on = false; };
  }, []);

  const canEdit = isFull && !!data?.editable;
  const months = data?.months || [];
  const buckets = data?.vertical_order || ["ATL/BTL", "Digital", "CX", "Other"];

  // ---- base maps -----------------------------------------------------------
  const baseMonth = useMemo(() => {
    const m = {};
    (data?.rows || []).forEach((r) => (r.months || []).forEach((mm) => {
      m[mKey(r.project, mm.month)] = { mgmt_budget: mm.mgmt_budget, mktg_mode: mm.mktg_mode, mktg_manual: mm.mktg_manual, spends: mm.spends };
    }));
    return m;
  }, [data]);

  const baseWeights = useMemo(() => {
    const m = {};
    (data?.rows || []).forEach((r) => { m[r.project] = r.weights || {}; });
    return m;
  }, [data]);

  // ---- monthly effective getters ------------------------------------------
  const cellEdit = (p, mo) => edits[mKey(p, mo)] || {};
  const effMgmt = (p, mo) => {
    const e = cellEdit(p, mo);
    if (e.mgmt_budget !== undefined) return lakhToRupees(e.mgmt_budget);
    return baseMonth[mKey(p, mo)]?.mgmt_budget ?? 0;
  };
  const effMode = (p, mo) => {
    const e = cellEdit(p, mo);
    if (e.mktg_mode !== undefined) return e.mktg_mode;
    return baseMonth[mKey(p, mo)]?.mktg_mode || "manual";
  };
  const effManual = (p, mo) => {
    const e = cellEdit(p, mo);
    if (e.mktg_manual !== undefined) return lakhToRupees(e.mktg_manual);
    return baseMonth[mKey(p, mo)]?.mktg_manual ?? 0;
  };
  const effPlanned = (p, mo) => (effMode(p, mo) === "auto" ? Math.round(0.8 * effMgmt(p, mo)) : effManual(p, mo));
  const cellSpends = (p, mo) => baseMonth[mKey(p, mo)]?.spends ?? 0;

  const setCell = (p, mo, patch) =>
    setEdits((prev) => ({ ...prev, [mKey(p, mo)]: { ...prev[mKey(p, mo)], ...patch } }));

  const mgmtInputVal = (p, mo) => {
    const e = cellEdit(p, mo);
    return e.mgmt_budget !== undefined ? e.mgmt_budget : toLakhStr(baseMonth[mKey(p, mo)]?.mgmt_budget ?? 0);
  };
  const manualInputVal = (p, mo) => {
    const e = cellEdit(p, mo);
    return e.mktg_manual !== undefined ? e.mktg_manual : toLakhStr(baseMonth[mKey(p, mo)]?.mktg_manual ?? 0);
  };

  // ---- derived per-project + totals (live with edits) ----------------------
  const derived = useMemo(() => {
    if (!data) return null;
    const rows = (data.rows || []).map((r) => {
      let mgmt = 0, planned = 0, spends = 0;
      (r.months || []).forEach((mm) => {
        mgmt += effMgmt(r.project, mm.month);
        planned += effPlanned(r.project, mm.month);
        spends += cellSpends(r.project, mm.month);
      });
      return { ...r, mgmt_budget: mgmt, marketing_planned: planned, spends, spare: mgmt - spends };
    });
    const totals = rows.reduce((a, r) => ({
      mgmt_budget: a.mgmt_budget + r.mgmt_budget,
      marketing_planned: a.marketing_planned + r.marketing_planned,
      spends: a.spends + r.spends,
    }), { mgmt_budget: 0, marketing_planned: 0, spends: 0 });
    totals.spare = totals.mgmt_budget - totals.spends;
    return { rows, totals };
  }, [data, edits]); // eslint-disable-line react-hooks/exhaustive-deps

  const monthlyDirty = Object.keys(edits).length;
  const weightsDirty = Object.keys(wedits).length;

  // ---- weights effective ---------------------------------------------------
  const weightVal = (p, b) => {
    const e = wedits[p];
    if (e && e[b] !== undefined) return e[b];
    return String(baseWeights[p]?.[b] ?? 0);
  };
  const weightSum = (p) => buckets.reduce((a, b) => a + (parseFloat(weightVal(p, b)) || 0), 0);
  const setWeight = (p, b, v) => setWedits((prev) => ({ ...prev, [p]: { ...(prev[p] || {}), [b]: v } }));

  // ---- save loops ----------------------------------------------------------
  const saveMonthly = async () => {
    const entries = Object.keys(edits).map((k) => {
      const [project, month] = k.split("||");
      return {
        project, month,
        mgmt_budget: effMgmt(project, month),
        mktg_mode: effMode(project, month),
        mktg_manual: effManual(project, month),
      };
    });
    if (!entries.length) return;
    setSaving(true);
    try {
      const res = await api.saveBudgetMonthly(entries);
      toast.success(`Saved ${res.updated ?? entries.length} monthly budget change(s)`, {
        description: "Dashboard KPIs now reflect these month-by-month figures.",
      });
      setEdits({});
      await load();
    } catch (e) {
      toast.error("Could not save monthly budgets", { description: e?.response?.data?.detail || "Please try again." });
    } finally { setSaving(false); }
  };

  const saveWeights = async () => {
    const entries = Object.keys(wedits).map((project) => {
      const weights = {};
      buckets.forEach((b) => { weights[b] = parseFloat(weightVal(project, b)) || 0; });
      return { project, weights };
    });
    if (!entries.length) return;
    setSaving(true);
    try {
      const res = await api.saveBudgetWeights(entries);
      toast.success(`Saved weights for ${res.updated ?? entries.length} project(s)`, {
        description: "Vertical breakdown of the monthly budgets updated.",
      });
      setWedits({});
      await load();
    } catch (e) {
      toast.error("Could not save vertical weights", { description: e?.response?.data?.detail || "Please try again." });
    } finally { setSaving(false); }
  };

  if (loading && !data) return <div className="p-4"><Loading rows={6} /></div>;

  const drows = derived?.rows || [];
  const t = derived?.totals || {};
  const isEmpty = drows.length === 0;

  return (
    <div className="space-y-4" data-testid="budget-allocation-tab">
      {/* headline KPIs (full-year rollup of the monthly entries) */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard label="Management Budget · FY" accent="blue" icon={PiggyBank}
          value={<Money value={t.mgmt_budget} />} sub="Sum of monthly budgets entered" testId="budget-kpi-mgmt" />
        <KpiCard label="Planned Marketing · FY" accent="teal" icon={Wallet}
          value={<Money value={t.marketing_planned} />} sub="80% auto or manual, per month" testId="budget-kpi-planned" />
        <KpiCard label="Actual Spends · FY" accent="amber" icon={Receipt}
          value={<Money value={t.spends} />} sub="Pulled live from invoices" testId="budget-kpi-spends" />
        <KpiCard label="Spare Budget" accent={(t.spare || 0) < 0 ? "red" : "green"} icon={Coins}
          value={<Money value={t.spare} colorNegative />}
          sub={(t.spare || 0) < 0 ? "Spent beyond budget" : "Management Budget − Spends"} testId="budget-kpi-spare" />
      </div>

      <Tabs value={view} onValueChange={setView}>
        <TabsList data-testid="budget-view-tabs">
          <TabsTrigger value="monthly" data-testid="budget-view-monthly"><Calendar className="mr-1.5 h-3.5 w-3.5" /> Monthly Plan</TabsTrigger>
          <TabsTrigger value="vertical" data-testid="budget-view-vertical"><PieChart className="mr-1.5 h-3.5 w-3.5" /> Vertical Split</TabsTrigger>
        </TabsList>

        {/* ============================= MONTHLY ============================ */}
        <TabsContent value="monthly" className="mt-4">
          <SectionCard
            title="Monthly Budget Matrix"
            description="Key in Management Budget and Planned Marketing budget per project, per month. Toggle Auto to set Planned = 80% of the Management Budget, or enter it manually. Spare = Management Budget − live Spends. These numbers drive the Dashboard KPIs for the selected month."
            testId="budget-monthly-card"
            action={
              canEdit ? (
                <div className="flex items-center gap-2">
                  {monthlyDirty > 0 && (
                    <Button variant="ghost" size="sm" className="h-8 gap-1.5 text-xs" onClick={() => setEdits({})} disabled={saving} data-testid="budget-monthly-reset">
                      <RotateCcw className="h-3.5 w-3.5" /> Discard
                    </Button>
                  )}
                  <Button size="sm" className="h-8 gap-1.5 text-xs" onClick={saveMonthly} disabled={saving || monthlyDirty === 0} data-testid="budget-monthly-save">
                    <Save className="h-3.5 w-3.5" /> {saving ? "Saving\u2026" : monthlyDirty > 0 ? `Save (${monthlyDirty})` : "Saved"}
                  </Button>
                </div>
              ) : (
                <span className="inline-flex items-center gap-1.5 rounded-md border bg-[hsl(var(--surface-2))] px-2 py-1 text-[11px] text-muted-foreground" data-testid="budget-monthly-locked">
                  <Lock className="h-3 w-3" /> Only a Leader/Admin can edit budgets
                </span>
              )
            }>
            {isEmpty ? (
              <div className="p-4"><EmptyState title="No projects" message="No projects available to budget." /></div>
            ) : (
              <div className="table-scroll">
                <table className="w-full text-sm" data-testid="budget-monthly-table">
                  <thead className="bg-[hsl(var(--surface-2))] text-[11px] uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="th-sticky-left px-3 py-2 text-left font-medium">Project / Month</th>
                      <th className="px-3 py-2 text-right font-medium">Mgmt Budget</th>
                      <th className="px-3 py-2 text-center font-medium">Auto 80%</th>
                      <th className="px-3 py-2 text-right font-medium">Planned</th>
                      <th className="px-3 py-2 text-right font-medium">Spends</th>
                      <th className="px-3 py-2 text-right font-medium">Spare</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {drows.map((r) => {
                      const isOpen = !!open[r.project];
                      return (
                        <React.Fragment key={r.project}>
                          <tr className="hover:bg-[hsl(var(--surface-2))]" data-testid={`budget-monthly-row-${r.project}`}>
                            <td className="td-sticky-left px-3 py-2.5">
                              <button type="button" onClick={() => setOpen((o) => ({ ...o, [r.project]: !o[r.project] }))}
                                className="inline-flex items-center gap-1.5 text-left font-medium hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] rounded" data-testid={`budget-monthly-expand-${r.project}`}>
                                <ChevronRight className={cn("h-3.5 w-3.5 text-muted-foreground transition-transform duration-150", isOpen && "rotate-90")} />
                                {r.label}
                              </button>
                              {r.category && <span className="ml-2 rounded bg-secondary px-1.5 py-0.5 text-[10px] text-muted-foreground">{r.category}</span>}
                            </td>
                            <td className="px-3 py-2.5 text-right tabular-nums text-muted-foreground"><Money value={r.mgmt_budget} /></td>
                            <td className="px-3 py-2.5" />
                            <td className="px-3 py-2.5 text-right tabular-nums"><Money value={r.marketing_planned} /></td>
                            <td className="px-3 py-2.5 text-right tabular-nums font-medium"><Money value={r.spends} /></td>
                            <td className={cn("px-3 py-2.5 text-right tabular-nums font-medium", spareCls(r.spare))}><Money value={r.spare} colorNegative /></td>
                          </tr>
                          {isOpen && months.map((mo) => {
                            const isAuto = effMode(r.project, mo) === "auto";
                            const sp = cellSpends(r.project, mo);
                            const spare = effMgmt(r.project, mo) - sp;
                            return (
                              <tr key={`${r.project}-${mo}`} className="bg-[hsl(var(--surface-2))] text-xs" data-testid={`budget-monthly-cell-${r.project}-${mo}`}>
                                <td className="td-sticky-left px-3 py-1.5 pl-8 text-muted-foreground">{mo}</td>
                                <td className="px-3 py-1.5 text-right">
                                  {canEdit ? (
                                    <div className="relative ml-auto w-[104px]">
                                      <Input type="number" inputMode="decimal" value={mgmtInputVal(r.project, mo)}
                                        onChange={(e) => setCell(r.project, mo, { mgmt_budget: e.target.value })}
                                        className="h-7 pr-5 text-right text-xs tabular-nums"
                                        data-testid={`budget-mgmt-${r.project}-${mo}`} />
                                      <span className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 text-[9px] text-muted-foreground">L</span>
                                    </div>
                                  ) : <Money value={effMgmt(r.project, mo)} />}
                                </td>
                                <td className="px-3 py-1.5 text-center">
                                  <Switch checked={isAuto} disabled={!canEdit}
                                    onCheckedChange={(v) => setCell(r.project, mo, { mktg_mode: v ? "auto" : "manual" })}
                                    data-testid={`budget-auto-${r.project}-${mo}`} />
                                </td>
                                <td className="px-3 py-1.5 text-right">
                                  {canEdit && !isAuto ? (
                                    <div className="relative ml-auto w-[104px]">
                                      <Input type="number" inputMode="decimal" value={manualInputVal(r.project, mo)}
                                        onChange={(e) => setCell(r.project, mo, { mktg_manual: e.target.value })}
                                        className="h-7 pr-5 text-right text-xs tabular-nums"
                                        data-testid={`budget-manual-${r.project}-${mo}`} />
                                      <span className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 text-[9px] text-muted-foreground">L</span>
                                    </div>
                                  ) : (
                                    <span className="inline-flex items-center gap-1 tabular-nums" data-testid={`budget-planned-${r.project}-${mo}`}>
                                      {isAuto && <Sparkles className="h-3 w-3 text-[hsl(var(--info))]" />}
                                      <Money value={effPlanned(r.project, mo)} />
                                    </span>
                                  )}
                                </td>
                                <td className="px-3 py-1.5 text-right tabular-nums text-muted-foreground"><Money value={sp} /></td>
                                <td className={cn("px-3 py-1.5 text-right tabular-nums", spareCls(spare))}><Money value={spare} colorNegative /></td>
                              </tr>
                            );
                          })}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 bg-[hsl(var(--surface-3))] font-semibold" data-testid="budget-monthly-totals">
                      <td className="td-sticky-left px-3 py-2.5">Grand Total</td>
                      <td className="px-3 py-2.5 text-right tabular-nums"><Money value={t.mgmt_budget} /></td>
                      <td className="px-3 py-2.5" />
                      <td className="px-3 py-2.5 text-right tabular-nums"><Money value={t.marketing_planned} /></td>
                      <td className="px-3 py-2.5 text-right tabular-nums"><Money value={t.spends} /></td>
                      <td className={cn("px-3 py-2.5 text-right tabular-nums", spareCls(t.spare))}><Money value={t.spare} colorNegative /></td>
                    </tr>
                  </tfoot>
                </table>
                {canEdit && (
                  <p className="px-3 py-2 text-[11px] text-muted-foreground" data-testid="budget-monthly-hint">
                    Tip: expand a project, then enter Mgmt Budget per month (values in Rs Lakhs). Turn on Auto 80% to set Planned automatically, or leave it off to type Planned manually.
                  </p>
                )}
              </div>
            )}
          </SectionCard>
        </TabsContent>

        {/* ============================= VERTICAL SPLIT ==================== */}
        <TabsContent value="vertical" className="mt-4">
          <SectionCard
            title="Vertical Split (weights)"
            description="Set how each project's monthly budget is split across ATL/BTL, Digital, CX & Other. Weights are percentages and should add up to 100%. The breakdown below applies these weights to the full-year monthly totals."
            testId="budget-weights-card"
            action={
              canEdit ? (
                <div className="flex items-center gap-2">
                  {weightsDirty > 0 && (
                    <Button variant="ghost" size="sm" className="h-8 gap-1.5 text-xs" onClick={() => setWedits({})} disabled={saving} data-testid="budget-weights-reset">
                      <RotateCcw className="h-3.5 w-3.5" /> Discard
                    </Button>
                  )}
                  <Button size="sm" className="h-8 gap-1.5 text-xs" onClick={saveWeights} disabled={saving || weightsDirty === 0} data-testid="budget-weights-save">
                    <Save className="h-3.5 w-3.5" /> {saving ? "Saving\u2026" : weightsDirty > 0 ? `Save (${weightsDirty})` : "Saved"}
                  </Button>
                </div>
              ) : (
                <span className="inline-flex items-center gap-1.5 rounded-md border bg-[hsl(var(--surface-2))] px-2 py-1 text-[11px] text-muted-foreground" data-testid="budget-weights-locked">
                  <Lock className="h-3 w-3" /> Only a Leader/Admin can edit weights
                </span>
              )
            }>
            {isEmpty ? (
              <div className="p-4"><EmptyState title="No projects" message="No projects available." /></div>
            ) : (
              <div className="table-scroll">
                <table className="w-full text-sm" data-testid="budget-weights-table">
                  <thead className="bg-[hsl(var(--surface-2))] text-[11px] uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="th-sticky-left px-3 py-2 text-left font-medium">Project</th>
                      {buckets.map((b) => (
                        <th key={b} className="px-3 py-2 text-right font-medium">
                          <span className="inline-flex items-center gap-1.5">
                            <span className="h-2 w-2 rounded-full" style={{ background: VERT_ACCENT[b] || "hsl(var(--primary))" }} />
                            {b} %
                          </span>
                        </th>
                      ))}
                      <th className="px-3 py-2 text-right font-medium">Sum</th>
                      <th className="px-3 py-2 text-right font-medium">FY Budget</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {drows.map((r) => {
                      const sum = weightSum(r.project);
                      const ok = Math.abs(sum - 100) < 0.01;
                      return (
                        <tr key={r.project} className="hover:bg-[hsl(var(--surface-2))]" data-testid={`budget-weights-row-${r.project}`}>
                          <td className="td-sticky-left px-3 py-2.5 font-medium">{r.label}</td>
                          {buckets.map((b) => (
                            <td key={b} className="px-3 py-2 text-right">
                              {canEdit ? (
                                <Input type="number" inputMode="decimal" value={weightVal(r.project, b)}
                                  onChange={(e) => setWeight(r.project, b, e.target.value)}
                                  className="ml-auto h-7 w-[72px] text-right text-xs tabular-nums"
                                  data-testid={`budget-weight-${r.project}-${b.replace(/[^a-zA-Z]+/g, "-")}`} />
                              ) : <span className="tabular-nums">{weightVal(r.project, b)}%</span>}
                            </td>
                          ))}
                          <td className="px-3 py-2.5 text-right">
                            <Badge variant="outline" className={cn("tabular-nums text-[10px]", ok ? "border-[hsl(var(--success)/0.4)] text-[hsl(var(--success))]" : "border-[hsl(var(--warning)/0.5)] text-[hsl(var(--warning))]")}
                              data-testid={`budget-weight-sum-${r.project}`}>
                              {Math.round(sum)}%
                            </Badge>
                          </td>
                          <td className="px-3 py-2.5 text-right tabular-nums text-muted-foreground"><Money value={r.mgmt_budget} /></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <p className="px-3 py-2 text-[11px] text-muted-foreground" data-testid="budget-weights-hint">
                  Weights split each project's monthly totals into the four money buckets for the vertical breakdown view. They don't change the total budget — only how it's distributed.
                </p>
              </div>
            )}
          </SectionCard>
        </TabsContent>
      </Tabs>
    </div>
  );
}
